/**
 * Per-destination permission gating for onboarding operational links.
 *
 * Every operational link on the onboarding workspace points at a
 * management screen. This module maps each ``OnboardingItem.key`` to the
 * MINIMUM effective role that can perform the corresponding write action
 * on the destination — NOT merely view it. A link is rendered only when
 * the caller can actually act on it; otherwise it's hidden so users
 * aren't misled into pages they cannot use.
 *
 * Backend authority (verified against ``backend/app/routers``):
 *
 * | Item                                 | Destination            | Write required |
 * |--------------------------------------|------------------------|----------------|
 * | company_configured                   | /admin/structure       | owner_admin    |
 * | branch_configured                    | /admin/structure       | owner_admin    |
 * | owner_admin_exists                   | /admin/users           | owner_admin    |
 * | manager_trainer_exists               | /admin/users           | owner_admin    |
 * | representative_exists                | /admin/users           | owner_admin    |
 * | pipeline_stages_ready                | /config (via template) | owner_admin    |
 * | contact_import_ready                 | /imports/new           | manager_trainer|
 * | training_material_ready              | /training              | owner_admin    |
 *
 * ``tenant_profile_confirmed`` and ``template_selected`` are handled in
 * the workspace itself (ack button; template panel) and never get an
 * operational link.
 */
import type { OnboardingItem } from "./contracts";
import type { EffectiveRole } from "../../../lib/roles";

export type LinkDestination =
  | "structure"
  | "users"
  | "pipeline"
  | "contact-import"
  | "training";

export interface OperationalLink {
  href: string;
  label: string;
  destination: LinkDestination;
  /** The role the caller MUST have to act on the destination. */
  requiredRole: "owner_admin" | "manager_trainer";
}

const RANK: Record<string, number> = {
  super_admin: 100,
  owner_admin: 80,
  manager_trainer: 60,
  representative: 40,
  viewer: 20,
};

/** Structural destination for an onboarding item, or ``null`` when the
 *  item has no operational link (ack items, template selection). */
export function destinationForItem(
  key: string,
): OperationalLink | null {
  switch (key) {
    case "company_configured":
    case "branch_configured":
      return {
        href: "/admin/structure",
        label: "Open Organization Structure",
        destination: "structure",
        requiredRole: "owner_admin",
      };
    case "owner_admin_exists":
    case "manager_trainer_exists":
    case "representative_exists":
      return {
        href: "/admin/users",
        label: "Open User Management",
        destination: "users",
        requiredRole: "owner_admin",
      };
    case "pipeline_stages_ready":
      return {
        href: "/config",
        label: "Open Pipeline Settings",
        destination: "pipeline",
        requiredRole: "owner_admin",
      };
    case "contact_import_ready":
      return {
        href: "/imports/new",
        label: "Open Contact Import",
        destination: "contact-import",
        requiredRole: "manager_trainer",
      };
    case "training_material_ready":
      return {
        href: "/training",
        label: "Open Training",
        destination: "training",
        requiredRole: "owner_admin",
      };
    default:
      return null;
  }
}

/** Resolves the operational link for an item, honouring the caller's
 *  effective role. Returns ``null`` when the caller cannot act on the
 *  destination — the workspace hides the link in that case rather than
 *  showing something the user cannot use. */
export function linkForRole(
  item: OnboardingItem,
  role: EffectiveRole,
): OperationalLink | null {
  const link = destinationForItem(item.key);
  if (!link) return null;
  if (!role) return null;
  const have = RANK[role] || 0;
  const need = RANK[link.requiredRole] || 0;
  return have >= need ? link : null;
}
